import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";

import {
  googleSession,
  IGoogleSessionToken,
  LANG_DEFAULT,
  Log,
  sessionStorageHelpers,
  SupportedLanguage,
} from "@ryde/shared";

import {
  fetchGoogleTileSessionWithRetry,
  getGoogleSessionStorageKey,
  isCachedGoogleSessionValid,
} from "./google-api-loader.utils";

interface IGoogleApiLoader {
  customId?: string;
  language: SupportedLanguage;
}

export const GoogleApiLoaderContainer: FC<IGoogleApiLoader> = ({
  language = LANG_DEFAULT,
}) => {
  const { t } = useTranslation();
  const setSession = useSetRecoilState(googleSession);

  useEffect(() => {
    let isCancelled = false;
    const shouldAbort = (): boolean => isCancelled;

    const init = async (): Promise<void> => {
      const sessionKey = getGoogleSessionStorageKey(language);
      const savedSession =
        sessionStorageHelpers.getItem<IGoogleSessionToken | null>(sessionKey);

      if (isCachedGoogleSessionValid(savedSession)) {
        if (!isCancelled) {
          setSession(savedSession);
        }
        return;
      }

      try {
        const newSession = await fetchGoogleTileSessionWithRetry({
          language,
          shouldAbort,
        });

        if (isCancelled) {
          return;
        }

        setSession(newSession);
        Log.debug("New Google title session created", language, newSession);
        sessionStorageHelpers.setItem<IGoogleSessionToken>(
          sessionKey,
          newSession,
        );
      } catch (error) {
        Log.error(t("errors.failedToLoadGoogleMapTiles"), error);
      }
    };

    void init();

    return () => {
      isCancelled = true;
    };
  }, [language, setSession, t]);

  return null;
};
